"use client";
import MarkdownPreview from "@uiw/react-markdown-preview";
import React, { cloneElement, useEffect, useState } from "react";
import { useTheme } from "next-themes";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import dayjs from "dayjs";
import { fromMarkdown } from "react-markdown-toc";
import { useRouter } from "next/navigation";
import TOCClient from "../toc-client";
import { cn } from "@/libs/utils";

const MarkDown = ({ title, source, SSRtheme }: any) => {
  const router = useRouter();
  const CSRtheme = useTheme();

  const [theme, setTheme] = useState(SSRtheme);

  const data = JSON.parse(source);
  const { date, tags } = data.data;

  const postDate = dayjs(date).format("YYYY-MM-DD");

  useEffect(() => {
    setTheme(CSRtheme.theme);
  }, [CSRtheme]);

  const toc = fromMarkdown(data.content);

  return (
    <div className="flex gap-4">
      <div className="flex flex-col w-full">
        <h1 className="inline-flex items-center justify-center font-bold mb-6 text-size-3xl">
          {title}
        </h1>

        <div className="flex items-center flex-col mb-8 gap-1 select-none">
          <time className="font-thin text-size-md text-muted-foreground">
            {postDate}
          </time>

          <div className="flex gap-2 mt-4">
            {tags?.map((tag: string, i: number) => (
              <span
                key={i}
                className="font-thin text-size-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <MarkdownPreview
          className="markdown-preview" // 여기에 클래스 추가
          source={data.content}
          style={{
            background: "transparent",
          }}
          wrapperElement={{
            "data-color-mode": theme,
          }}
          remarkPlugins={[remarkGfm, remarkBreaks]}
          rehypePlugins={[rehypeRaw]}
          rehypeRewrite={(node: any, index, parent) => {
            if (node.tagName === "img") {
              node.properties.src = `${process.env.NEXT_PUBLIC_BLOG_CF_ENDPOINT}/files/${node.properties.src}`;
            }
          }}
          components={{
            // strong: ({ children }) => (
            //   <strong className="mb-0">{children}</strong>
            // ),
            a: ({ href, children }) => {
              const isExternal = href?.startsWith("http");
              return (
                <a
                  href={href}
                  {...(isExternal && {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })}
                >
                  {children}
                </a>
              );
            },
            ol: ({ children }) => (
              <ol style={{ listStyleType: "decimal" }}>{children}</ol>
            ),
            ul: ({ children }) => (
              <ul style={{ paddingLeft: "1em", listStyleType: "disc" }}>
                {children}
              </ul>
            ),
            li: ({ children }) => <li className="text-size-sm">{children}</li>,
            h1: ({ children }) => <h1 className="text-size-3xl">{children}</h1>,
            h2: ({ children }) => <h2 className="text-size-2xl">{children}</h2>,
            h3: ({ children }) => <h3 className="text-lg">{children}</h3>,
            h4: ({ children }) => <h4 className="text-base">{children}</h4>,
            p: ({ children }) => {
              const childrenArray = React.Children.toArray(children);
              const hasStrong = childrenArray.some(
                (child: any) =>
                  typeof child === "object" && child?.type === "strong"
              );

              return (
                <p
                  className={cn(
                    "text-size-sm dark:text-[#d1d5db] leading-7",
                    hasStrong && "!m-0"
                  )}
                >
                  {children}
                </p>
              );
            },
            img: ({ children, src, alt }) => {
              let displayAlt = alt || "";
              let width: string | undefined;
              let height: string | undefined;
              let align: string | undefined;

              if (alt && alt.includes("|")) {
                const [altText, optionsStr] = alt.split("|");
                displayAlt = altText.trim();

                const options = optionsStr.split(",").map((o) => o.trim());
                for (const option of options) {
                  const sizeMatch = option.match(/^(\d+)x(\d+)$/);
                  if (sizeMatch) {
                    width = sizeMatch[1];
                    height = sizeMatch[2];
                  } else if (option === "center") {
                    align = "center";
                  }
                }
              }

              return (
                <figure
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    ...(align === "center" && { alignItems: "center" }),
                  }}
                >
                  <img
                    className="flex"
                    src={src}
                    alt={displayAlt}
                    style={{
                      ...(width && { width: `${width}px` }),
                      ...(height && { height: `${height}px` }),
                    }}
                  >
                    {children}
                  </img>
                  {displayAlt && (
                    <em
                      className="text-size-xs"
                      style={{
                        textAlign: "center",
                        ...(width && { width: `${width}px` }),
                      }}
                    >
                      {displayAlt}
                    </em>
                  )}
                </figure>
              );
            },
            br: () => <br className="w-full h-2" />,
            code: ({ children, className, node }: any) => {
              const isDiffArray = ["object", "string"];
              if (isDiffArray.includes(typeof children)) {
                return (
                  <code className={cn("w-full", className)}>{children}</code>
                );
              }

              const childrenWithProps = children.map((item: JSX.Element) => {
                return cloneElement(item, {
                  className: cn("whitespace-pre-wrap", item.props.className),
                });
              });

              return <code className={className}>{childrenWithProps}</code>;
            },
          }}
        />
      </div>

      <div className="relative max-md:hidden">
        <ul className="sticky top-24 p-4 border-l-2 min-w-64">
          <TOCClient toc={toc} />
        </ul>
      </div>
    </div>
  );
};

export default MarkDown;
